/**
 * ExtendAmcEndDateDialog (C.3 Commit 3 · §4.1 Controlled Operational Drift)
 *
 * Super-Admin-only flow for extending a PO-derived AMC schedule's end_date
 * forward, under the §4.1 governance carve-out. Operator must:
 *   1. Pick a new end_date strictly later than the current one.
 *   2. Supply a meaningful free-text reason (≥ 8 non-whitespace chars).
 *   3. Review the scheduler-impact preview (exact ₹ / period / frequency).
 *   4. Confirm — server-side enforces the cumulative cap and writes
 *      the audit trail + extension_history event.
 *
 * Backend:
 *   - POST /api/billing/amc/{id}/preview-extension  (read-only impact preview)
 *   - PATCH /api/billing/amc/{id}  (end_date + extension_reason payload)
 */
import { useEffect, useMemo, useState } from "react";
import { Calendar, Warning, CheckCircle, Lock } from "@phosphor-icons/react";
import { api, formatApiError } from "../lib/api";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Textarea } from "./ui/textarea";
import { DialogFooter } from "./ui/dialog";
import FormDialog, { FormDialogCancel } from "./FormDialog";
import { toast } from "sonner";

const formatMoney = (amount, symbol = "₹") => {
    const num = Number(amount) || 0;
    return `${symbol}${num.toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
};

const todayIso = () => new Date().toISOString().slice(0, 10);

export default function ExtendAmcEndDateDialog({ open, onOpenChange, sched, onSaved }) {
    const [newEnd, setNewEnd] = useState("");
    const [reason, setReason] = useState("");
    const [preview, setPreview] = useState(null);
    const [previewing, setPreviewing] = useState(false);
    const [confirming, setConfirming] = useState(false);
    const [previewError, setPreviewError] = useState("");

    const currentEnd = useMemo(() => (sched?.end_date || "").slice(0, 10), [sched]);
    const originalEnd = useMemo(
        () => (sched?.original_end_date || currentEnd || "").slice(0, 10),
        [sched, currentEnd]
    );
    const cumulativeUsed = sched?.cumulative_extension_days || 0;
    const cumulativeCap = 365;
    const remainingCap = Math.max(0, cumulativeCap - cumulativeUsed);
    const reasonValid = reason.trim().length >= 8;
    const newEndValid = newEnd && newEnd > currentEnd;

    // Reset state when dialog re-opens for a different schedule.
    useEffect(() => {
        if (open) {
            setNewEnd("");
            setReason("");
            setPreview(null);
            setPreviewError("");
        }
    }, [open, sched?.id]);

    const runPreview = async () => {
        if (!newEndValid) return;
        setPreviewing(true);
        setPreviewError("");
        setPreview(null);
        try {
            const r = await api.post(`/billing/amc/${sched.id}/preview-extension`, {
                new_end_date: newEnd,
            });
            setPreview(r.data);
        } catch (e) {
            setPreviewError(formatApiError(e));
        } finally {
            setPreviewing(false);
        }
    };

    const onConfirm = async () => {
        if (!reasonValid || !newEndValid) return;
        if (preview?.would_exceed_cap) {
            toast.error("Cumulative cap exceeded — use Renewal Lifecycle instead.");
            return;
        }
        setConfirming(true);
        try {
            const r = await api.patch(`/billing/amc/${sched.id}`, {
                end_date: newEnd,
                extension_reason: reason.trim(),
            });
            toast.success(
                `Extended to ${(r.data.end_date || "").slice(0, 10)} · ${r.data.cumulative_extension_days || 0} days of ${cumulativeCap} used.`
            );
            if (onSaved) onSaved(r.data);
            onOpenChange(false);
        } catch (e) {
            toast.error(formatApiError(e));
        } finally {
            setConfirming(false);
        }
    };

    if (!sched) return null;

    const overCap = preview?.would_exceed_cap;

    return (
        <FormDialog
            open={open}
            onOpenChange={onOpenChange}
            title="Extend AMC end date (§4.1 Controlled Extension)"
            description={
                <>
                    Forward-only extension of a PO-derived AMC schedule. The
                    cumulative extension across all events is capped at{" "}
                    <strong>{cumulativeCap} days</strong>; reductions require
                    AMC close + new schedule + credit note.
                </>
            }
            contentClassName="max-w-3xl"
            testId={`extend-amc-dialog-${sched.id}`}
        >
            <div className="space-y-5">
                {/* Context strip */}
                <div className="grid grid-cols-3 gap-3" data-testid="extend-context-strip">
                    <div className="rounded-lg border border-slate-200 bg-slate-50 p-3">
                        <div className="text-xs uppercase tracking-wide text-slate-500">Current end date</div>
                        <div className="mt-1 text-base font-semibold text-slate-900" data-testid="extend-current-end">
                            {currentEnd || "—"}
                        </div>
                    </div>
                    <div className="rounded-lg border border-slate-200 bg-slate-50 p-3">
                        <div className="text-xs uppercase tracking-wide text-slate-500">Original end date</div>
                        <div className="mt-1 text-base font-semibold text-slate-900" data-testid="extend-original-end">
                            {originalEnd || currentEnd || "—"}
                        </div>
                    </div>
                    <div
                        className={`rounded-lg border p-3 ${remainingCap <= 30 ? "border-amber-300 bg-amber-50" : "border-slate-200 bg-slate-50"}`}
                    >
                        <div className="text-xs uppercase tracking-wide text-slate-500">Cumulative cap</div>
                        <div className="mt-1 text-base font-semibold text-slate-900" data-testid="extend-cap-usage">
                            {cumulativeUsed} <span className="text-slate-500">/ {cumulativeCap} days used</span>
                        </div>
                        <div className="mt-1 text-xs text-slate-500">{remainingCap} days remaining</div>
                    </div>
                </div>

                {/* Inputs */}
                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                    <div>
                        <Label className="text-xs font-medium uppercase tracking-wide text-slate-600">
                            New end date
                        </Label>
                        <Input
                            type="date"
                            min={currentEnd || todayIso()}
                            value={newEnd}
                            onChange={(e) => {
                                setNewEnd(e.target.value);
                                setPreview(null);
                            }}
                            data-testid="extend-new-end-input"
                        />
                        {newEnd && !newEndValid && (
                            <div className="mt-1 text-xs text-rose-600" data-testid="extend-forward-only-warning">
                                Forward-only: must be strictly later than {currentEnd}.
                            </div>
                        )}
                    </div>
                    <div className="flex items-end">
                        <Button
                            type="button"
                            variant="secondary"
                            onClick={runPreview}
                            disabled={!newEndValid || previewing}
                            className="w-full"
                            data-testid="extend-preview-button"
                        >
                            <Calendar size={14} className="mr-2" />
                            {previewing ? "Computing…" : "Preview scheduler impact"}
                        </Button>
                    </div>
                </div>

                <div>
                    <Label className="text-xs font-medium uppercase tracking-wide text-slate-600">
                        Reason (mandatory · ≥ 8 chars · stored on audit trail)
                    </Label>
                    <Textarea
                        rows={3}
                        value={reason}
                        onChange={(e) => setReason(e.target.value)}
                        placeholder="e.g. Client confirmed scope expansion through Q4 per addendum; bill an additional cycle."
                        data-testid="extend-reason-input"
                    />
                    {reason.length > 0 && !reasonValid && (
                        <div className="mt-1 text-xs text-rose-600">
                            Reason is too short ({reason.trim().length} chars). Minimum 8 non-whitespace chars.
                        </div>
                    )}
                </div>

                {/* Preview card */}
                {previewError && (
                    <div
                        className="rounded-lg border border-rose-200 bg-rose-50 p-3 text-sm text-rose-900"
                        data-testid="extend-preview-error"
                    >
                        <Warning size={16} className="mr-2 inline" />
                        {previewError}
                    </div>
                )}
                {preview && !previewError && (
                    <div
                        className={`rounded-lg border p-4 ${overCap ? "border-rose-300 bg-rose-50" : "border-amber-300 bg-amber-50"}`}
                        data-testid="extend-impact-card"
                    >
                        <div className="flex items-start gap-3">
                            {overCap ? (
                                <Lock size={20} weight="fill" className="mt-0.5 text-rose-600" />
                            ) : (
                                <Warning size={20} weight="fill" className="mt-0.5 text-amber-600" />
                            )}
                            <div className="flex-1 text-sm leading-relaxed text-slate-900">
                                <div className="font-medium" data-testid="extend-impact-warning">
                                    {preview.warning_message}
                                </div>
                                {overCap && (
                                    <div className="mt-2 text-rose-800" data-testid="extend-cap-warning">
                                        This extension would push the cumulative total to{" "}
                                        <strong>{preview.cumulative_extension_days_after} days</strong>, exceeding
                                        the <strong>{preview.cumulative_cap_days}-day cap</strong>. Use Renewal
                                        Lifecycle instead (Commit 4, pending).
                                    </div>
                                )}
                                {!overCap && preview.projected_invoices?.length > 0 && (
                                    <div className="mt-3 space-y-1 text-xs text-slate-700">
                                        <div className="font-semibold uppercase tracking-wide text-slate-500">
                                            Cycles unlocked under this extension
                                        </div>
                                        {preview.projected_invoices.map((pi, idx) => (
                                            <div
                                                key={idx}
                                                className="flex items-center justify-between rounded border border-amber-200 bg-white px-2 py-1"
                                                data-testid={`extend-projected-cycle-${idx}`}
                                            >
                                                <span>
                                                    <strong>{pi.billing_frequency}</strong> · {pi.cycle_start} →{" "}
                                                    {pi.cycle_end}
                                                </span>
                                                <span className="font-mono">
                                                    {formatMoney(pi.total_amount, pi.currency_symbol)}
                                                </span>
                                            </div>
                                        ))}
                                    </div>
                                )}
                                <div className="mt-3 text-xs text-slate-600">
                                    After this extension: <strong>{preview.cumulative_extension_days_after} / {preview.cumulative_cap_days} days used</strong> (
                                    {preview.remaining_days_under_cap} remaining).
                                </div>
                            </div>
                        </div>
                    </div>
                )}
            </div>

            <DialogFooter className="mt-6">
                <FormDialogCancel disabled={confirming} testId="extend-cancel-button">
                    Cancel
                </FormDialogCancel>
                <Button
                    type="button"
                    onClick={onConfirm}
                    disabled={!reasonValid || !newEndValid || overCap || confirming}
                    data-testid="extend-confirm-button"
                >
                    <CheckCircle size={14} className="mr-2" />
                    {confirming ? "Saving…" : "Confirm extension"}
                </Button>
            </DialogFooter>
        </FormDialog>
    );
}
